import { DialogM } from "@common/components/alert/dialog/Dialog"
import { SnackBarM } from "@common/components/alert/snackbar/SnackBar"
import FilledButtonM from "@common/components/button/FilledButtonM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { IS_WEB } from "@common/utils/mobileUtils"
import { notifyBugsnag } from "@common/utils/initBugsnag"
import {
    prepareRecordingSession,
    reassertLiveSession,
    releaseRecordingSession,
} from "../../../engine/shared/audioSession/audioSessionCoordinator"
import { downloadToCache } from "@common/utils/downloadToCache"
import { displayTimeSeconds } from "@data/utility/Utils"
import Slider from "@react-native-community/slider"
import {
    RecordingPresets,
    requestRecordingPermissionsAsync,
    useAudioPlayer,
    useAudioPlayerStatus,
    useAudioRecorder,
    useAudioRecorderState,
} from "expo-audio"
import { useKeepAwake } from "expo-keep-awake"
import React, { useEffect, useRef, useState } from "react"
import { Vibration, View } from "react-native"
import { IconButton, TouchableRipple } from "react-native-paper"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { useSelector } from "react-redux"

const DISABLED_OPACITY = 0.5
const MAX_RECORDING_DURATION_MINUTES = 12

export const _getMMSSFromMillis = (millis) => {
    const totalSeconds = millis / 1000
    const seconds = Math.floor(totalSeconds % 60)
    const minutes = Math.floor(totalSeconds / 60)

    const padWithZero = (number) => {
        const string = number.toString()
        if (number < 10) {
            return "0" + string
        }
        return string
    }
    return padWithZero(minutes) + ":" + padWithZero(seconds)
}

// The app only records/sends m4a; default to it. iOS picks its parser from the
// file extension, so an explicit, correct one is what makes the local copy
// playable — an extension-less or signed URL otherwise yields a mislabelled
// file the native player refuses to load.
const remoteAudioExtension = (uri) => {
    const match = /\.(m4a|mp4|mp3|wav|aac|caf|webm|ogg)(?:\?|#|$)/i.exec(uri)
    return match ? `.${match[1].toLowerCase()}` : ".m4a"
}

// MediaRecorder defaults to WebM/Opus in Chromium and Firefox, which iOS
// AVPlayer cannot decode — voice notes sent from a web tutor came through
// as `blob_<uuid>.webm` and stayed unplayable on iPhone (the play button
// never enabled). Pick an MP4/AAC variant whenever the browser can write
// one (Safari always; Chromium 130+ on most platforms). Firefox can't
// write MP4 today and keeps the WebM fallback — iPhone playback for that
// minority would need server-side transcoding.
const WEB_RECORDER_PREFERRED_MIME_TYPES = [
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
    "audio/aac",
]

const pickWebRecorderMimeType = () => {
    if (!IS_WEB || typeof MediaRecorder === "undefined") return null
    for (const type of WEB_RECORDER_PREFERRED_MIME_TYPES) {
        if (MediaRecorder.isTypeSupported(type)) return type
    }
    return null
}

const WEB_RECORDER_MIME_TYPE = pickWebRecorderMimeType()

const RECORDER_OPTIONS = WEB_RECORDER_MIME_TYPE
    ? {
          ...RecordingPresets.HIGH_QUALITY,
          web: {
              ...RecordingPresets.HIGH_QUALITY.web,
              mimeType: WEB_RECORDER_MIME_TYPE,
          },
      }
    : RecordingPresets.HIGH_QUALITY

/**
 * Playback layer. Lifecycle is owned by expo-audio's hooks: the player is
 * created/replaced when the source changes and released on unmount — no
 * manual pause/remove sequencing, no status listener to (de)register.
 */
const usePlaybackController = (recordingURI, status) => {
    const isRemote = recordingURI ? /^https?:/i.test(recordingURI) : false
    // On native, expo-audio can't load the messenger's audio endpoint as a
    // direct stream (attachment disposition, no Range → never reaches
    // isLoaded), and its built-in downloadFirst routes through expo-asset,
    // which forces a wrong extension on the extension-less/signed URL and
    // produces a file iOS won't parse. So we fetch the remote file ourselves
    // to a correctly-named local cache file and play that — local files load
    // reliably. Web keeps streaming the URL directly (the media element
    // handles it; a JS download would need CORS on the asset route).
    const needsLocalCopy = isRemote && !IS_WEB
    const [localUri, setLocalUri] = useState(null)

    useEffect(() => {
        setLocalUri(null)
        if (!needsLocalCopy || !recordingURI) return
        let cancelled = false
        downloadToCache(recordingURI, {
            prefix: "messenger-audio-",
            extension: remoteAudioExtension(recordingURI),
        })
            .then((uri) => {
                if (!cancelled) setLocalUri(uri)
            })
            .catch((e) => {
                console.log("messenger audio download failed", e?.message)
            })
        return () => {
            cancelled = true
        }
    }, [recordingURI, needsLocalCopy])

    const effectiveUri = needsLocalCopy ? localUri : recordingURI
    const source = effectiveUri ? { uri: effectiveUri } : null

    const player = useAudioPlayer(source, {
        updateInterval: 250,
        // Never let this player deactivate the shared AVAudioSession: active
        // recorders and live calls depend on it, and expo-audio tears it down
        // as soon as no player is playing (it never checks recorders).
        keepAudioSessionActive: true,
    })
    // Live status straight from the hook — no listeners, no stale closures.
    const playerStatus = useAudioPlayerStatus(player)

    const pause = () => {
        try {
            player.pause()
        } catch (e) {
            // Already released — ignore.
        }
    }

    useEffect(() => {
        if (source) {
            player.loop = true
        }
        // Belt and braces: the hook releases the player natively, but pausing
        // on replacement/unmount guarantees an in-flight loop stops even if a
        // platform implementation defers the release.
        return pause
    }, [player])

    useEffect(() => {
        if (status === "finished") pause()
    }, [status])

    const loaded = !!source && !!playerStatus?.isLoaded
    // expo-audio reports playback times in SECONDS; the UI helpers work in ms.
    const positionMs = loaded ? (playerStatus.currentTime ?? 0) * 1000 : null
    const durationMs = loaded ? (playerStatus.duration ?? 0) * 1000 : null
    const playing = loaded && playerStatus.playing
    const rate = playerStatus?.playbackRate || 1

    return {
        hasSource: !!source,
        loaded,
        playing,
        positionMs,
        durationMs,
        rate,
        togglePlayPause: () => {
            if (!loaded) return
            if (playing) player.pause()
            else player.play()
        },
        seekToRatio: (ratio) => {
            if (!loaded || !durationMs) return
            player.seekTo((ratio * durationMs) / 1000)
        },
        cycleRate: () => {
            if (!loaded) return
            const next = rate >= 2 ? 1 : rate >= 1.5 ? 2 : 1.5
            player.setPlaybackRate(next, "medium")
            player.shouldCorrectPitch = true
        },
    }
}

const PlaybackRow = ({ playback, status, color, fallbackDuration }) => {
    const timestamp =
        playback.positionMs != null && playback.durationMs != null
            ? `${_getMMSSFromMillis(playback.positionMs)} / ${
                  Number.isFinite(playback.durationMs) && playback.durationMs > 0
                      ? _getMMSSFromMillis(playback.durationMs)
                      : displayTimeSeconds(fallbackDuration ?? 0)
              }`
            : fallbackDuration
              ? displayTimeSeconds(fallbackDuration)
              : "-"

    const disabled = !playback.loaded || status === "finished"
    const sliderPosition =
        playback.loaded && playback.durationMs
            ? playback.positionMs / playback.durationMs
            : 0

    return (
        <View
            style={{
                opacity: !playback.loaded ? DISABLED_OPACITY : 1.0,
                width: "100%",
                alignItems: "center",
            }}
        >
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    alignSelf: "stretch",
                }}
            >
                <IconButton
                    icon={playback.playing ? "pause" : "play"}
                    iconColor={color ? color : theme.colors.primary}
                    containerColor={theme.colors.transparent}
                    size={30}
                    onPress={playback.togglePlayPause}
                    disabled={disabled}
                />
                <View
                    style={{
                        flex: 1,
                        marginTop: 20,
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Slider
                        style={{ alignSelf: "stretch" }}
                        minimumTrackTintColor={
                            color ? color : theme.colors.primary
                        }
                        thumbTintColor={color ? color : theme.colors.primary}
                        maximumTrackTintColor={theme.colors.grey}
                        value={sliderPosition}
                        onSlidingComplete={playback.seekToRatio}
                        disabled={disabled}
                    />
                    <TextM
                        fontWeight="Medium"
                        fontSize={16}
                        color={color ? color : theme.colors.grey}
                    >
                        {timestamp}
                    </TextM>
                </View>
                <TouchableRipple
                    onPress={playback.cycleRate}
                    underlayColor={theme.colors.lighter}
                    disabled={disabled}
                    style={{
                        marginLeft: 6,
                        borderWidth: 1,
                        borderColor: theme.colors.lightMore,
                        borderRadius: 10,
                        alignItems: "center",
                        width: 45,
                        paddingHorizontal: 8,
                        paddingVertical: 5,
                    }}
                >
                    <TextM color={theme.colors.light}>
                        {`${playback.rate ?? 1}x`}
                    </TextM>
                </TouchableRipple>
            </View>
        </View>
    )
}

/**
 * Read-only playback (chat bubbles, logs, finished activities). No recorder
 * object is ever constructed here.
 */
const AudioPlaybackView = ({ recordingURI, status, color, duration }) => {
    const playback = usePlaybackController(recordingURI, status)

    if (!playback.hasSource) return null
    return (
        <View style={{ alignItems: "center", width: "100%", marginTop: 5 }}>
            <PlaybackRow
                playback={playback}
                status={status}
                color={color}
                fallbackDuration={duration}
            />
        </View>
    )
}

/**
 * Full record → preview → submit flow. The recorder is created by
 * expo-audio's hook (correctly flattened platform options, web support
 * built in); all audio-session transitions go through the coordinator.
 */
const AudioRecordFlow = (props) => {
    const {
        recordingURI,
        setRecordingURI,
        status,
        setTime = () => {},
        duration,
        chatMode = false,
        color = null,
        onSend = () => {},
        activityPaused = false,
        onDeleteRecording = () => {},
        audioRecordingStatus = () => {},
        minRecordingDurationMs = 0,
    } = props

    if (!IS_WEB) useKeepAwake("AudioRecording")

    const isLiveModeEnabled = useSelector(
        (state) => state.liveMode?.isLiveModeEnabled
    )
    const isLiveRef = useRef(isLiveModeEnabled)
    useEffect(() => {
        isLiveRef.current = isLiveModeEnabled
    }, [isLiveModeEnabled])

    const recorder = useAudioRecorder(RECORDER_OPTIONS)
    // The screen only shows MM:SS — no need for a native status read every
    // 100 ms for as long as the form is mounted.
    const recorderState = useAudioRecorderState(recorder, 500)
    // True between startRecording and stop/delete — drives the record UI.
    const [recordingActive, setRecordingActive] = useState(false)
    // True while the audio session is configured for recording, so unmount
    // knows whether it has a session to hand back.
    const sessionHeldRef = useRef(false)
    const releaseHeldSession = () => {
        if (!sessionHeldRef.current) return
        sessionHeldRef.current = false
        releaseRecordingSession(isLiveRef.current).catch(() => {})
    }

    const playback = usePlaybackController(recordingURI, status)

    const isBelowMinDuration =
        minRecordingDurationMs > 0 &&
        (recorderState.durationMillis ?? 0) < minRecordingDurationMs

    const startRecording = async () => {
        try {
            const response = await requestRecordingPermissionsAsync()
            if (!response.granted) {
                DialogM.show({
                    text1: "Attention",
                    text2: "Vous devez autoriser l'application à enregistrer des contenus audio.",
                })
                return
            }
            if (recordingURI) setRecordingURI(null)

            await prepareRecordingSession(isLiveRef.current)
            sessionHeldRef.current = true
            await recorder.prepareToRecordAsync()
            // prepare() stomps the iOS session config; during a live, restore
            // one that both LiveKit and the recorder can work with.
            await reassertLiveSession(isLiveRef.current)
            recorder.record()
            setRecordingActive(true)
        } catch (err) {
            // Said and reported: swallowed, a refused start (expo-audio's
            // RecordingDisabledException) left a mic button that did nothing,
            // with no trace anywhere. A snackbar, not a dialog: the chat hosts
            // this recorder in a modal, which a dialog would render behind.
            notifyBugsnag(err, {
                where: "AudioRecordingContainer.startRecording",
                isLive: !!isLiveRef.current,
            })
            releaseHeldSession()
            SnackBarM.show({
                text1: "L'enregistrement n'a pas démarré : réessaie dans un instant.",
                duration: 5000,
            })
        }
    }

    const stopRecording = async () => {
        const recordedMs = recorderState.durationMillis ?? 0
        setTime(Math.floor(recordedMs / 1000))

        if (recordedMs >= MAX_RECORDING_DURATION_MINUTES * 60 * 1000)
            DialogM.show({
                text1: "Durée max atteinte",
                text2:
                    "Les enregistrements audio ne peuvent pas dépasser " +
                    MAX_RECORDING_DURATION_MINUTES +
                    " minutes",
            })

        let stopped = true
        try {
            await recorder.stop()
        } catch (error) {
            console.log("STOP ERROR: ", error?.code, error?.message)
            stopped = false
        } finally {
            // Hand the session back on every exit path: left in the recording
            // configuration it keeps ducking and attenuating every later sound
            // until another recording happens to complete.
            await releaseRecordingSession(isLiveRef.current)
            sessionHeldRef.current = false
            setRecordingActive(false)
        }
        if (!stopped) return
        setRecordingURI(recorder.uri)
        audioRecordingStatus("recorded")
    }

    const validateRecording = () => {
        if (isBelowMinDuration) {
            if (recorderState.isRecording) recorder.pause()
            DialogM.show({
                text1: "Commentaire trop court",
                text2: "Dis quelques mots : ce qui a été vu, les points forts, ce qu'il reste à travailler.",
            })
            return
        }
        Vibration.vibrate(50)
        stopRecording()
    }

    const togglePauseRecording = () => {
        try {
            if (recorderState.isRecording) recorder.pause()
            else recorder.record()
        } catch (error) {
            console.log("PAUSE/RESUME ERROR: ", error?.code, error?.message)
        }
    }

    const deleteRecording = () => {
        onDeleteRecording()
        setRecordingURI(null)
        audioRecordingStatus("deleted")
    }

    // Auto-stop at the maximum duration.
    useEffect(() => {
        if (
            recordingActive &&
            recorderState.isRecording &&
            (recorderState.durationMillis ?? 0) >=
                MAX_RECORDING_DURATION_MINUTES * 60 * 1000
        ) {
            Vibration.vibrate(50)
            validateRecording()
        }
    }, [recorderState.durationMillis])

    useEffect(() => {
        if (activityPaused && recorderState.isRecording) recorder.pause()
    }, [activityPaused])

    // Leaving the screen mid-recording (back / navigation) must not strand the
    // session in the recording configuration: the hook releases the recorder,
    // but only the coordinator hands the session back.
    useEffect(() => {
        return releaseHeldSession
    }, [])

    const buttonSize = 70
    const showPlayer = playback.hasSource && !recordingActive

    return (
        <View
            style={{
                alignItems: "center",
                width: "100%",
                marginTop: 30,
            }}
        >
            {!showPlayer && (
                <View
                    style={{
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "row",
                        width: "100%",
                    }}
                >
                    <View style={{ flexDirection: "row", alignItems: "center" }}>
                        <TextM
                            fontWeight="Medium"
                            fontSize={45}
                            color={theme.colors.grey}
                        >
                            {_getMMSSFromMillis(
                                recorderState.durationMillis ?? 0
                            )}
                        </TextM>
                    </View>
                </View>
            )}

            {showPlayer && (
                <PlaybackRow
                    playback={playback}
                    status={status}
                    color={color}
                    fallbackDuration={duration}
                />
            )}

            <TextM
                fontWeight="Medium"
                fontSize={14}
                color={theme.colors.error}
                style={{ marginTop: 15, marginBottom: 10 }}
            >
                {recordingActive
                    ? recorderState.isRecording
                        ? "Enregistrement en cours"
                        : "En pause"
                    : ""}
            </TextM>

            {status !== "finished" && (
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <FilledButtonM
                        color={
                            recordingActive
                                ? theme.colors.error
                                : showPlayer
                                  ? theme.colors.white
                                  : theme.colors.error
                        }
                        onPress={() => {
                            Vibration.vibrate(50)
                            if (recordingActive) togglePauseRecording()
                            else if (showPlayer) deleteRecording()
                            else startRecording()
                        }}
                        contentStyle={{
                            width: buttonSize,
                            height: buttonSize,
                            borderRadius: buttonSize / 2,
                        }}
                        style={{
                            width: buttonSize,
                            borderRadius: buttonSize / 2,
                        }}
                        label={
                            <FontAwesome5
                                name={
                                    recordingActive
                                        ? recorderState.isRecording
                                            ? "pause"
                                            : "microphone"
                                        : showPlayer
                                          ? "trash"
                                          : "microphone"
                                }
                                size={buttonSize / 3}
                                color={
                                    showPlayer
                                        ? theme.colors.grey
                                        : theme.colors.white
                                }
                            />
                        }
                    />
                    {recordingActive && (
                        <FilledButtonM
                            color={theme.colors.white}
                            onPress={validateRecording}
                            contentStyle={{
                                width: buttonSize,
                                height: buttonSize,
                                borderRadius: buttonSize / 2,
                            }}
                            style={{
                                width: buttonSize,
                                borderRadius: buttonSize / 2,
                                marginLeft: 15,
                            }}
                            label={
                                <FontAwesome5
                                    name={"check"}
                                    size={(2 * buttonSize) / 5}
                                    color={theme.colors.success}
                                />
                            }
                        />
                    )}
                    {chatMode && showPlayer && (
                        <FilledButtonM
                            color={theme.colors.white}
                            onPress={onSend}
                            contentStyle={{
                                width: buttonSize,
                                height: buttonSize,
                                borderRadius: buttonSize / 2,
                            }}
                            style={{
                                width: buttonSize,
                                borderRadius: buttonSize / 2,
                                marginLeft: 15,
                            }}
                            label={
                                <FontAwesome5
                                    solid
                                    name={"paper-plane"}
                                    size={(2 * buttonSize) / 5}
                                    color={theme.colors.success}
                                />
                            }
                        />
                    )}
                </View>
            )}
        </View>
    )
}

export const AudioRecordingContainer = (props) => {
    // Read-only consumers (chat bubbles, step logs…) get a pure player —
    // they never construct a recorder.
    if (props.readOnly) return <AudioPlaybackView {...props} />
    return <AudioRecordFlow {...props} />
}
